import React, { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	DEFAULT_NUDGE_WIDTH,
	JWM_FEEDBACK_TASK,
} from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import {
	POSITION_LEFT_START,
	POSITION_BOTTOM,
} from '@atlassian/jira-onboarding-quickstart-core/src/common/ui/instrumented-nudge-spotlight-card/constants.tsx';
import { QuickstartNudge } from '@atlassian/jira-onboarding-quickstart-core/src/common/ui/quickstart-nudge/index.tsx';
import type { QuickstartNudgeWrapperBaseProps } from '@atlassian/jira-onboarding-quickstart-core/src/common/ui/quickstart-nudge/types.tsx';
import { useMarkAsCompleteAndExpand } from '@atlassian/jira-onboarding-quickstart-core/src/controllers/mark-as-complete-and-expand/index.tsx';
import { useQuickstartActions } from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTrackMountedNudgesController } from '@atlassian/jira-software-onboarding-quickstart-track-mounted-nudges/src/index.tsx';
import { FEEDBACK_NUDGE_ID } from '../../common/constants.tsx';
import { useCanShowQuickstartNudge } from '../../controllers/quickstart-nudge/index.tsx';
import messages from './messages.tsx';

export const FeedbackNudge = ({ children }: QuickstartNudgeWrapperBaseProps) => {
	useTrackMountedNudgesController(FEEDBACK_NUDGE_ID);
	const { formatMessage } = useIntl();
	const shouldShowNudge = useCanShowQuickstartNudge();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [, { setHasNudgeActive }] = useQuickstartActions();
	const markComplete = useMarkAsCompleteAndExpand();

	const onFinishClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'finishNudgeButton clicked', {
			nudgeId: FEEDBACK_NUDGE_ID,
		});
		setHasNudgeActive(false, JWM_FEEDBACK_TASK);
		markComplete(JWM_FEEDBACK_TASK);
	}, [createAnalyticsEvent, markComplete, setHasNudgeActive]);

	if (!shouldShowNudge) {
		return <>{children}</>;
	}

	return (
		<QuickstartNudge
			nudgeId={FEEDBACK_NUDGE_ID}
			itemNames={[JWM_FEEDBACK_TASK]}
			nudgeSpotlightCardProps={{
				content: formatMessage(messages.jwmFeedbackNudgeContentSpork),
				width: DEFAULT_NUDGE_WIDTH,
				position:
					getWillShowNav4() && fg('blu-5788-spotlight-help-button')
						? POSITION_LEFT_START
						: POSITION_BOTTOM, // Now that this is attached to the Help button in the topNav, we need to position the message to the left
				actionsBeforeElement: '1/1',
				actions: [
					{
						text: formatMessage(messages.nudgeCtaFinish),
						onClick: onFinishClick,
					},
				],
			}}
		>
			{children}
		</QuickstartNudge>
	);
};
